import { DateOnly } from '@if/core/date'
import { EApplicantType, EFinancingProgram, EOriginSystemId } from '@if/core/types'
import { CreateLoanStatusDto } from '@src/data/types/CreateLoanStatusDto'
import { getApiClient } from '@src/services/api-client'
import { getContentDispositionFilename, triggerFileDownload } from '@src/services/file-download'
import { useCallback } from 'react'
import {
  MutationFunction,
  QueryObserverResult,
  RefetchOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import {
  CreditApplication,
  CreditApplicationReservation,
  CreditApplicationReservationDto,
  CreditApplicationTask,
  FullCreditApplication,
  ProofsOfRelease,
} from '../../types'
import { UpdateCreditApplicationMerchantDto } from '../../types/UpdateCreditApplicationMerchantDto'
import { creditApplicationHistoryKeys } from '../credit-application-history/credit-application-history-api'
import { documentKeys } from '../document/document-api'
import { fullCreditApplicationKeys, useInvalidateFullCreditApplication } from '../full-credit-application/full-credit-application-api'
import transformFullCreditApplicationFromApi from '../full-credit-application/full-credit-application-transform'
import { FullCreditApplicationDto } from '../full-credit-application/FullCreditApplicationDto'
import { CopyCreditApplicationAsDraftDto } from './CopyCreditApplicationAsDraftDto'
import {
  transformCreditApplicationFromApi,
  transformCreditApplicationToApi,
  transformEditIncomesDtoToApi,
} from './credit-transform'
import { EditBankAccountDto } from './EditBankAccountDto'
import { EditIncomesDto } from './EditIncomesDto'
import { RefinanceCreditApplicationDto } from './RefinanceCreditApplicationDto'
import { AnyError } from '@if/mui'

const SCOPE = 'credit-application'
const DETAIL = 'detail'
const LOCK_SCOPE = 'credit-application-lock'

const keysFactory = {
  all: [SCOPE] as const,
  detail: (financingProgramId: string, creditApplicationId: string) =>
    [SCOPE, DETAIL, financingProgramId, creditApplicationId] as const,
  lock: (financingProgramId: string, creditApplicationId: string) =>
    [LOCK_SCOPE, financingProgramId, creditApplicationId] as const,
}

// ==================== QUERY & MUTATION FUNCTIONS ====================

const getCreateLoanStatus = async (financingProgramId: string, creditApplicationId: string) => {
  const response = await getApiClient().get<CreateLoanStatusDto>(
    `/Applications/${encodeURIComponent(financingProgramId)}/${encodeURIComponent(creditApplicationId)}/LoanCreationStatus`,
  )
  return response.data
}

export function useGetCreateLoanStatus(
  financingProgramId: string,
  creditApplicationId: string,
  shouldPoll: boolean,
): [
  CreateLoanStatusDto | null,
  boolean,
  refetch: (options?: RefetchOptions) => Promise<QueryObserverResult<CreateLoanStatusDto, Error>>,
] {
  const { isFetching, data, refetch } = useQuery({
    queryKey: keysFactory.detail(financingProgramId, creditApplicationId),
    queryFn: () => getCreateLoanStatus(financingProgramId, creditApplicationId),
    enabled: !!creditApplicationId && !!financingProgramId && shouldPoll,
    refetchInterval: shouldPoll ? 5000 : undefined,
    staleTime: 5000,
    gcTime: 5000,
  })
  return [data ?? null, isFetching, refetch]
}

const sendHighEstimateFundingConfirmation = async ({
  financingProgramId,
  creditApplicationId,
}: {
  financingProgramId: EFinancingProgram
  creditApplicationId: string
}) => {
  const response = await getApiClient().post<void>(
    `/Applications/${encodeURIComponent(financingProgramId)}/${encodeURIComponent(creditApplicationId)}/SendHighEstimateFundingConfirmation`,
  )
  return response.data
}

export function useSendHighEstimateFundingConfirmation(
  financingProgramId: EFinancingProgram,
  creditApplicationId: string,
): [MutationFunction<void, void>, boolean] {
  const { isPending, mutateAsync } = useMutation({
    mutationFn: () => sendHighEstimateFundingConfirmation({ financingProgramId, creditApplicationId }),
  })

  return [mutateAsync, isPending]
}

export interface FirstPaymentDateOptionsDto {
  deliveryOn: DateOnly
  systemOriginId: EOriginSystemId
  isHighEstimate: boolean
  paymentFrequency: string
}

const getFirstPaymentDateOptions = async ({
  financingProgramId,
  creditApplicationId,
  firstPaymentDateOptionsDto,
}: {
  financingProgramId: EFinancingProgram
  creditApplicationId: string
  firstPaymentDateOptionsDto: FirstPaymentDateOptionsDto
}): Promise<Date[]> => {
  const response = await getApiClient().post<Date[]>(
    `/Applications/${encodeURIComponent(financingProgramId)}/${encodeURIComponent(creditApplicationId)}/FirstPaymentOptions`,
    firstPaymentDateOptionsDto,
  )
  return response.data
}

// Pass `null` for `firstPaymentDateOptionsDto` to disable the hook entirely —
// no API call, returns empty data. Use this when prerequisite fields (e.g.
// activationDate, paymentFrequency) aren't filled yet.
export function useGetFirstPaymentDateOptions(
  financingProgramId: EFinancingProgram | undefined,
  creditApplicationId: string | undefined,
  firstPaymentDateOptionsDto: FirstPaymentDateOptionsDto | null,
): [Date[], boolean] {
  const { data, isFetching } = useQuery({
    queryKey: ['firstPaymentDateOptions', financingProgramId, creditApplicationId, firstPaymentDateOptionsDto],
    queryFn: () =>
      getFirstPaymentDateOptions({
        financingProgramId: financingProgramId!,
        creditApplicationId: creditApplicationId!,
        firstPaymentDateOptionsDto: firstPaymentDateOptionsDto!,
      }),
    enabled: !!financingProgramId && !!creditApplicationId && !!firstPaymentDateOptionsDto,
  })

  return [data ?? [], isFetching]
}

// Get Credit Application by ID
export async function getCreditApplication(
  financingProgramId: string,
  creditApplicationId: string,
): Promise<CreditApplication> {
  const apiClient = getApiClient()
  const response = await apiClient.get<CreditApplication>(
    `/Applications/${encodeURIComponent(financingProgramId)}/${encodeURIComponent(creditApplicationId)}`,
  )
  return transformCreditApplicationFromApi(response.data)
}

export function useGetCreditApplication(financingProgramId?: string, creditApplicationId?: string) {
  return useQuery({
    queryKey: keysFactory.detail(financingProgramId ?? '', creditApplicationId ?? ''),
    queryFn: () => getCreditApplication(financingProgramId!, creditApplicationId!),
    enabled: !!financingProgramId && !!creditApplicationId,
  })
}

// ==================== MUTATION HOOKS ====================

// Create Credit Application
const createCreditApplication = async (data: Partial<CreditApplication>) => {
  const apiClient = getApiClient()
  const transformed = transformCreditApplicationToApi(data as CreditApplication)
  const response = await apiClient.post<CreditApplication>(
    `/Applications/${encodeURIComponent(data.financingProgramId!)}`,
    transformed,
  )
  return transformCreditApplicationFromApi(response.data)
}

export function useCreateCreditApplication(): [
  (data: Partial<CreditApplication>) => Promise<CreditApplication>,
  boolean,
] {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending } = useMutation({
    mutationFn: createCreditApplication,
    onSuccess: (createdCredit) => {
      void queryClient.invalidateQueries({
        queryKey: fullCreditApplicationKeys.detail(createdCredit.id, createdCredit.financingProgramId),
      })
    },
  })

  return [mutateAsync, isPending]
}

// Update Credit Application
const updateCreditApplication = async (data: Partial<CreditApplication>) => {
  const apiClient = getApiClient()
  const transformed = transformCreditApplicationToApi(data as CreditApplication)
  const response = await apiClient.put<CreditApplication>(
    `/Applications/${encodeURIComponent(data.financingProgramId!)}/${encodeURIComponent(data.id!)}`,
    transformed,
  )
  return transformCreditApplicationFromApi(response.data)
}

export function useUpdateCreditApplication(): [
  (data: Partial<CreditApplication>) => Promise<CreditApplication>,
  boolean,
] {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending } = useMutation({
    mutationFn: updateCreditApplication,
    onSuccess: (updatedCredit) => {
      const queryKey = fullCreditApplicationKeys.detail(updatedCredit.id, updatedCredit.financingProgramId)
      const currentData = queryClient.getQueryData<FullCreditApplication>(queryKey)
      if (currentData) {
        queryClient.setQueryData<FullCreditApplication>(queryKey, {
          ...currentData,
          credit: updatedCredit,
        })
      }
    },
  })

  return [mutateAsync, isPending]
}

// Update Merchant
const updateMerchant = async (data: UpdateCreditApplicationMerchantDto) => {
  const apiClient = getApiClient()
  const response = await apiClient.put<CreditApplication>(
    `/Applications/${encodeURIComponent(data.financingProgramId)}/${encodeURIComponent(data.creditApplicationId)}/Merchant`,
    data,
  )
  return transformCreditApplicationFromApi(response.data)
}

export function useUpdateMerchant(): [
  (data: UpdateCreditApplicationMerchantDto) => Promise<CreditApplication>,
  boolean,
] {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending } = useMutation({
    mutationFn: updateMerchant,
    onSuccess: (updatedCredit, params) => {
      const queryKey = fullCreditApplicationKeys.detail(params.creditApplicationId, params.financingProgramId)
      const currentData = queryClient.getQueryData<FullCreditApplication>(queryKey)
      if (currentData) {
        queryClient.setQueryData<FullCreditApplication>(queryKey, {
          ...currentData,
          credit: transformCreditApplicationFromApi(updatedCredit),
        })
      }
    },
  })

  return [mutateAsync, isPending]
}

// Confirm All Incomes
interface ConfirmAllIncomesDto {
  id: string
  financingProgramId: EFinancingProgram
}

const confirmAllIncomes = async (data: ConfirmAllIncomesDto) => {
  const apiClient = getApiClient()
  const response = await apiClient.post<CreditApplicationTask>(
    `/Applications/${encodeURIComponent(data.financingProgramId)}/${encodeURIComponent(data.id)}/IncomeConfirmation`,
  )
  return response.data
}

export function useConfirmAllIncomes(): [(data: ConfirmAllIncomesDto) => Promise<CreditApplicationTask>, boolean] {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending } = useMutation({
    mutationFn: confirmAllIncomes,
    onSuccess: (newTask, params) => {
      const queryKey = fullCreditApplicationKeys.detail(params.id, params.financingProgramId)
      const currentData = queryClient.getQueryData<FullCreditApplication>(queryKey)
      if (currentData) {
        queryClient.setQueryData<FullCreditApplication>(queryKey, {
          ...currentData,
          tasks: [...currentData.tasks, newTask],
        })
      }
    },
  })

  return [mutateAsync, isPending]
}

// Refresh Credit Report
interface RefreshCreditReportDto {
  creditApplicationId: string
  financingProgramId: EFinancingProgram
  isApplicant: boolean
  versionTag: string
  requestOriginId: EOriginSystemId
}

const refreshCreditReport = async (data: RefreshCreditReportDto) => {
  const apiClient = getApiClient()
  const response = await apiClient.put<CreditApplication>(
    `/Applications/${encodeURIComponent(data.financingProgramId)}/${encodeURIComponent(data.creditApplicationId)}/RefreshCreditReport`,
    {
      creditApplicationId: data.creditApplicationId,
      isApplicant: data.isApplicant,
      versionTag: data.versionTag,
      requestOriginId: data.requestOriginId,
    },
  )
  return transformCreditApplicationFromApi(response.data)
}

export function useRefreshCreditReport(): [(data: RefreshCreditReportDto) => Promise<CreditApplication>, boolean] {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending } = useMutation({
    mutationFn: refreshCreditReport,
    onSuccess: (updatedCredit, params) => {
      const queryKey = fullCreditApplicationKeys.detail(params.creditApplicationId, params.financingProgramId)
      const currentData = queryClient.getQueryData<FullCreditApplication>(queryKey)
      if (currentData) {
        queryClient.setQueryData<FullCreditApplication>(queryKey, {
          ...currentData,
          credit: updatedCredit,
        })
      }
    },
  })

  return [mutateAsync, isPending]
}

// Change Status
interface ChangeStatusDto {
  financingProgramId: EFinancingProgram
  creditApplicationId: string
  status: string
  versionTag: string
}

const changeStatus = async (data: ChangeStatusDto) => {
  const apiClient = getApiClient()
  const response = await apiClient.put<CreditApplication>(
    `/Applications/${encodeURIComponent(data.financingProgramId)}/${encodeURIComponent(data.creditApplicationId)}/status`,
    { status: data.status, versionTag: data.versionTag },
  )
  return transformCreditApplicationFromApi(response.data)
}

export function useChangeStatus(): [(data: ChangeStatusDto) => Promise<CreditApplication>, boolean] {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending } = useMutation({
    mutationFn: changeStatus,
    onSuccess: (updatedCredit, params) => {
      const queryKey = fullCreditApplicationKeys.detail(params.creditApplicationId, params.financingProgramId)
      const currentData = queryClient.getQueryData<FullCreditApplication>(queryKey)
      if (currentData) {
        queryClient.setQueryData<FullCreditApplication>(queryKey, {
          ...currentData,
          credit: updatedCredit,
        })
      }
    },
  })

  return [mutateAsync, isPending]
}

// Change Rush
interface ChangeRushDto {
  financingProgramId: EFinancingProgram
  creditApplicationId: string
  isRush: boolean
  versionTag: string
}

const changeRush = async (data: ChangeRushDto) => {
  const apiClient = getApiClient()
  const response = await apiClient.put<CreditApplication>(
    `/Applications/${encodeURIComponent(data.financingProgramId)}/${encodeURIComponent(data.creditApplicationId)}/rush`,
    { isRush: data.isRush, versionTag: data.versionTag },
  )
  return transformCreditApplicationFromApi(response.data)
}

export function useChangeRush(): [(data: ChangeRushDto) => Promise<CreditApplication>, boolean] {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending } = useMutation({
    mutationFn: changeRush,
    onSuccess: (updatedCredit, params) => {
      const queryKey = fullCreditApplicationKeys.detail(params.creditApplicationId, params.financingProgramId)
      const currentData = queryClient.getQueryData<FullCreditApplication>(queryKey)
      if (currentData) {
        queryClient.setQueryData<FullCreditApplication>(queryKey, {
          ...currentData,
          credit: updatedCredit,
        })
      }
    },
  })

  return [mutateAsync, isPending]
}

// Update Incomes
const updateIncomes = async (data: EditIncomesDto) => {
  const apiClient = getApiClient()
  const transformed = transformEditIncomesDtoToApi(data)
  const response = await apiClient.put<CreditApplication>(
    `/Applications/${encodeURIComponent(data.financingProgramId)}/${encodeURIComponent(data.creditApplicationId)}/${encodeURIComponent(data.applicantType)}/incomes`,
    transformed,
  )
  return transformCreditApplicationFromApi(response.data)
}

export function useUpdateIncomes(): [(data: EditIncomesDto) => Promise<CreditApplication>, boolean] {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending } = useMutation({
    mutationFn: updateIncomes,
    onSuccess: (updatedCredit, params) => {
      const queryKey = fullCreditApplicationKeys.detail(params.creditApplicationId, params.financingProgramId)
      const currentData = queryClient.getQueryData<FullCreditApplication>(queryKey)
      if (currentData) {
        queryClient.setQueryData<FullCreditApplication>(queryKey, {
          ...currentData,
          credit: updatedCredit,
        })
      }
      void queryClient.invalidateQueries({
        queryKey: creditApplicationHistoryKeys.incomesHistory(params.creditApplicationId, params.financingProgramId),
      })
    },
  })

  return [mutateAsync, isPending]
}

// Download Credit PDF
const downloadCreditPdf = async (creditApplication: CreditApplication): Promise<void> => {
  const apiClient = getApiClient()
  const response = await apiClient.post<Blob>(
    `/Applications/${encodeURIComponent(creditApplication.financingProgramId)}/DownloadCreditApplicationPDF`,
    creditApplication,
    { responseType: 'blob' },
  )

  const filename = getContentDispositionFilename(
    response.headers as Record<string, string | undefined>,
    'credit-application.pdf',
  )
  triggerFileDownload(new Blob([response.data], { type: 'application/pdf' }), filename)
}

export function useDownloadCreditPdf(): [(data: CreditApplication) => Promise<void>, boolean] {
  const { mutateAsync, isPending } = useMutation({
    mutationFn: downloadCreditPdf,
  })

  return [mutateAsync, isPending]
}

// Lock Credit Application
interface LockCreditApplicationDto {
  creditApplicationId: string
  financingProgramId: EFinancingProgram
}

const lockCreditApplication = async (data: LockCreditApplicationDto) => {
  const apiClient = getApiClient()
  const response = await apiClient.put<CreditApplication>(
    `/Applications/${encodeURIComponent(data.financingProgramId)}/${encodeURIComponent(data.creditApplicationId)}/LockEdit`,
  )
  return transformCreditApplicationFromApi(response.data)
}

export function useLockCreditApplication(): [(data: LockCreditApplicationDto) => Promise<CreditApplication>, boolean] {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending } = useMutation({
    mutationFn: lockCreditApplication,
    onSuccess: (updatedCredit, params) => {
      const queryKey = fullCreditApplicationKeys.detail(params.creditApplicationId, params.financingProgramId)
      const currentData = queryClient.getQueryData<FullCreditApplication>(queryKey)
      if (currentData) {
        queryClient.setQueryData<FullCreditApplication>(queryKey, {
          ...currentData,
          credit: updatedCredit,
        })
      }
    },
  })

  return [mutateAsync, isPending]
}

// Unlock Credit Application
const unlockCreditApplication = async (data: LockCreditApplicationDto) => {
  const apiClient = getApiClient()
  const response = await apiClient.put<CreditApplication>(
    `/Applications/${encodeURIComponent(data.financingProgramId)}/${encodeURIComponent(data.creditApplicationId)}/UnlockEdit`,
  )
  return transformCreditApplicationFromApi(response.data)
}

export function useUnlockCreditApplication(): [
  (data: LockCreditApplicationDto) => Promise<CreditApplication>,
  boolean,
] {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending } = useMutation({
    mutationFn: unlockCreditApplication,
    onSuccess: (updatedCredit, params) => {
      const queryKey = fullCreditApplicationKeys.detail(params.creditApplicationId, params.financingProgramId)
      const currentData = queryClient.getQueryData<FullCreditApplication>(queryKey)
      if (currentData) {
        queryClient.setQueryData<FullCreditApplication>(queryKey, {
          ...currentData,
          credit: updatedCredit,
        })
      }
    },
  })

  return [mutateAsync, isPending]
}

// Pay Out
const payOut = async (data: LockCreditApplicationDto) => {
  const apiClient = getApiClient()
  await apiClient.put(
    `/Applications/${encodeURIComponent(data.financingProgramId)}/${encodeURIComponent(data.creditApplicationId)}/PayOut`,
  )
}

export function usePayOut(): [(data: LockCreditApplicationDto) => Promise<void>, boolean] {
  const { mutateAsync, isPending } = useMutation({
    mutationFn: payOut,
  })

  return [mutateAsync, isPending]
}

// Download FCF Document (from document-api)
const downloadFCFDocument = async (
  creditApplicationId: string,
  financingProgramId: EFinancingProgram,
): Promise<void> => {
  const apiClient = getApiClient()
  const response = await apiClient.post<Blob>(
    `/Applications/${encodeURIComponent(financingProgramId)}/${encodeURIComponent(creditApplicationId)}/Documents/FCF/Download`,
    {},
    { responseType: 'blob' },
  )

  const filename = getContentDispositionFilename(
    response.headers as Record<string, string | undefined>,
    'fcf-document.pdf',
  )
  triggerFileDownload(new Blob([response.data], { type: 'application/pdf' }), filename)
}

export function useDownloadFCFDocument(): [
  (creditApplicationId: string, financingProgramId: EFinancingProgram) => Promise<void>,
  boolean,
] {
  const { mutateAsync, isPending } = useMutation({
    mutationFn: ({
      creditApplicationId,
      financingProgramId,
    }: {
      creditApplicationId: string
      financingProgramId: EFinancingProgram
    }) => downloadFCFDocument(creditApplicationId, financingProgramId),
  })

  const download = (creditApplicationId: string, financingProgramId: EFinancingProgram) => {
    return mutateAsync({ creditApplicationId, financingProgramId })
  }

  return [download, isPending]
}

// Download High Estimate Confirmation (PDF version of the confirmation email)
const downloadHighEstimateConfirmation = async (
  creditApplicationId: string,
  financingProgramId: EFinancingProgram,
): Promise<void> => {
  const apiClient = getApiClient()
  const response = await apiClient.post<Blob>(
    `/Applications/${encodeURIComponent(financingProgramId)}/${encodeURIComponent(creditApplicationId)}/Documents/HighEstimate/Download`,
    {},
    { responseType: 'blob' },
  )

  const filename = getContentDispositionFilename(
    response.headers as Record<string, string | undefined>,
    'high-estimate-confirmation.pdf',
  )
  triggerFileDownload(new Blob([response.data], { type: 'application/pdf' }), filename)
}

export function useDownloadHighEstimateConfirmation(): [
  (creditApplicationId: string, financingProgramId: EFinancingProgram) => Promise<void>,
  boolean,
] {
  const { mutateAsync, isPending } = useMutation({
    mutationFn: ({
      creditApplicationId,
      financingProgramId,
    }: {
      creditApplicationId: string
      financingProgramId: EFinancingProgram
    }) => downloadHighEstimateConfirmation(creditApplicationId, financingProgramId),
  })

  const download = useCallback(
    (creditApplicationId: string, financingProgramId: EFinancingProgram) => {
      return mutateAsync({ creditApplicationId, financingProgramId })
    },
    [mutateAsync],
  )

  return [download, isPending]
}

// Create Credit Application Lock (Reservation)
const createCreditApplicationLock = async (data: CreditApplicationReservationDto) => {
  const apiClient = getApiClient()
  const response = await apiClient.post<CreditApplicationReservation>(
    `/Applications/${encodeURIComponent(data.financingProgramId)}/${encodeURIComponent(data.creditApplicationId)}/Reservation`,
    data,
  )
  return response.data
}

export function useCreateCreditApplicationLock(): [
  (data: CreditApplicationReservationDto) => Promise<CreditApplicationReservation>,
  boolean,
] {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending } = useMutation({
    mutationFn: createCreditApplicationLock,
    onSuccess: (createdLock, params) => {
      const queryKey = keysFactory.lock(params.financingProgramId, params.creditApplicationId)
      queryClient.setQueryData(queryKey, createdLock)
    },
  })

  return [mutateAsync, isPending]
}

// Update Credit Application Lock
const updateCreditApplicationLock = async (data: CreditApplicationReservation) => {
  const apiClient = getApiClient()
  const response = await apiClient.put<CreditApplicationReservation>(
    `/Applications/${encodeURIComponent(data.financingProgramId)}/${encodeURIComponent(data.creditApplicationId)}/Reservation`,
    data,
  )
  return response.data
}

export function useUpdateCreditApplicationLock(): [
  (data: CreditApplicationReservation) => Promise<CreditApplicationReservation>,
  boolean,
] {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending } = useMutation({
    mutationFn: updateCreditApplicationLock,
    onSuccess: (updatedLock, params) => {
      const queryKey = keysFactory.lock(params.financingProgramId, params.creditApplicationId)
      queryClient.setQueryData(queryKey, updatedLock)
    },
  })

  return [mutateAsync, isPending]
}

// Delete Credit Application Lock
const deleteCreditApplicationLock = async (data: CreditApplicationReservation) => {
  const apiClient = getApiClient()
  const response = await apiClient.delete<CreditApplicationReservation>(
    `/Applications/${encodeURIComponent(data.financingProgramId)}/${encodeURIComponent(data.creditApplicationId)}/Reservation`,
    { data },
  )
  return response.data
}

export function useDeleteCreditApplicationLock(): [
  (data: CreditApplicationReservation) => Promise<CreditApplicationReservation>,
  boolean,
] {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending } = useMutation({
    mutationFn: deleteCreditApplicationLock,
    onSuccess: (deletedLock, params) => {
      const queryKey = keysFactory.lock(params.financingProgramId, params.creditApplicationId)
      queryClient.setQueryData(queryKey, deletedLock)
    },
  })

  return [mutateAsync, isPending]
}

// Update Proof of Release
const updateProofOfRelease = async (data: ProofsOfRelease) => {
  const apiClient = getApiClient()
  const response = await apiClient.put<CreditApplication>(
    `/Applications/${encodeURIComponent(data.financingProgramId)}/${encodeURIComponent(data.creditApplicationId)}/ProofOfRelease`,
    data,
  )
  return transformCreditApplicationFromApi(response.data)
}

export function useUpdateProofOfRelease(): [(data: ProofsOfRelease) => Promise<CreditApplication>, boolean] {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending } = useMutation({
    mutationFn: updateProofOfRelease,
    onSuccess: (updatedCredit, params) => {
      const queryKey = fullCreditApplicationKeys.detail(params.creditApplicationId, params.financingProgramId)
      const currentData = queryClient.getQueryData<FullCreditApplication>(queryKey)
      if (currentData) {
        queryClient.setQueryData<FullCreditApplication>(queryKey, {
          ...currentData,
          credit: updatedCredit,
        })
      }
    },
  })

  return [mutateAsync, isPending]
}

// Set Payment Account
const setPaymentAccount = async (data: EditBankAccountDto): Promise<FullCreditApplication> => {
  const apiClient = getApiClient()
  const response = await apiClient.put<FullCreditApplicationDto>(
    `/Applications/${encodeURIComponent(data.financingProgramId)}/${encodeURIComponent(data.creditApplicationId)}/PaymentAccount`,
    data,
  )
  return transformFullCreditApplicationFromApi(response.data)
}

export function useSetPaymentAccount(): [(data: EditBankAccountDto) => Promise<FullCreditApplication>, boolean] {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending } = useMutation({
    mutationFn: setPaymentAccount,
    onSuccess: (fullCreditApp, params) => {
      const queryKey = fullCreditApplicationKeys.detail(params.creditApplicationId, params.financingProgramId)
      queryClient.setQueryData<FullCreditApplication>(queryKey, fullCreditApp)
    },
  })

  return [mutateAsync, isPending]
}

// Refinance Credit Application
const refinanceCreditApplication = async (data: RefinanceCreditApplicationDto) => {
  const apiClient = getApiClient()
  const response = await apiClient.post<CreditApplication>(
    `/Applications/${encodeURIComponent(data.financingProgramId)}/${encodeURIComponent(data.creditApplicationId)}/Refinancing`,
    data,
  )
  return transformCreditApplicationFromApi(response.data)
}

export function useRefinanceCreditApplication(): [
  (data: RefinanceCreditApplicationDto) => Promise<CreditApplication>,
  boolean,
] {
  const { mutateAsync, isPending } = useMutation({
    mutationFn: refinanceCreditApplication,
  })

  return [mutateAsync, isPending]
}

// Copy Credit Application as Draft
const copyCreditApplicationAsDraft = async (data: CopyCreditApplicationAsDraftDto) => {
  const apiClient = getApiClient()
  const response = await apiClient.post<CreditApplication>(
    `/Applications/${encodeURIComponent(data.financingProgramId)}/${encodeURIComponent(data.creditApplicationId)}/CopyAsDraft`,
    data,
  )
  return transformCreditApplicationFromApi(response.data)
}

export function useCopyCreditApplicationAsDraft(): [
  (data: CopyCreditApplicationAsDraftDto) => Promise<CreditApplication>,
  boolean,
] {
  const { mutateAsync, isPending } = useMutation({
    mutationFn: copyCreditApplicationAsDraft,
  })

  return [mutateAsync, isPending]
}

// Reset Vouched
interface ResetVouchedDto {
  creditApplicationId: string
  financingProgramId: EFinancingProgram
  applicantType: EApplicantType
}

const resetVouched = async (data: ResetVouchedDto) => {
  const apiClient = getApiClient()
  await apiClient.post(
    `/Applications/${encodeURIComponent(data.financingProgramId)}/${encodeURIComponent(data.creditApplicationId)}/${encodeURIComponent(data.applicantType)}/ResetVouched`,
  )
}

export function useResetVouched(): [(data: ResetVouchedDto) => Promise<void>, boolean] {
  const { mutateAsync, isPending } = useMutation({
    mutationFn: resetVouched,
  })

  return [mutateAsync, isPending]
}

// Bypass Vouched (test-only)
export type BypassVouchedOutcome = 'success' | 'failure'

interface BypassVouchedDto {
  creditApplicationId: string
  financingProgramId: EFinancingProgram
  applicantType: EApplicantType
  outcome: BypassVouchedOutcome
}

const bypassVouched = async (data: BypassVouchedDto) => {
  const apiClient = getApiClient()
  await apiClient.post(
    `/Applications/${encodeURIComponent(data.creditApplicationId)}/${encodeURIComponent(data.applicantType)}/BypassVouched`,
    { outcome: data.outcome },
  )
}

export function useBypassVouched(): [(data: BypassVouchedDto) => Promise<void>, boolean, AnyError] {
  const queryClient = useQueryClient()
  const invalidateFullCreditApplication = useInvalidateFullCreditApplication()
  const { mutateAsync, isPending, error } = useMutation({
    mutationFn: bypassVouched,
    onSuccess: async (_, params) => {
      await invalidateFullCreditApplication(params.creditApplicationId, params.financingProgramId)
      await queryClient.invalidateQueries({
        queryKey: documentKeys.pages(params.creditApplicationId, params.financingProgramId),
      })
    },
  })

  return [mutateAsync, isPending, error]
}

// Get Credit Application Lock
const getCreditApplicationLock = async (
  financingProgramId: EFinancingProgram,
  creditApplicationId: string,
): Promise<CreditApplicationReservation> => {
  const apiClient = getApiClient()
  const response = await apiClient.get<CreditApplicationReservation>(
    `/Applications/${encodeURIComponent(financingProgramId)}/${encodeURIComponent(creditApplicationId)}/Reservation`,
  )
  return response.data
}

export function useGetCreditApplicationLock(
  financingProgramId: EFinancingProgram | undefined,
  creditApplicationId: string | undefined,
): [CreditApplicationReservation | null, boolean] {
  const { data, isFetching } = useQuery({
    queryKey: keysFactory.lock(financingProgramId ?? '', creditApplicationId ?? ''),
    queryFn: () => getCreditApplicationLock(financingProgramId!, creditApplicationId!),
    enabled: !!financingProgramId && !!creditApplicationId,
  })

  return [data ?? null, isFetching]
}

// ==================== CREDIT REPORT APIS ====================

const getCreditReportByCreditReportId = async (
  financingProgramId: string,
  creditApplicationId: string,
  creditReportId: string,
): Promise<{ creditReport: unknown }> => {
  const apiClient = getApiClient()
  const url = `/Applications/${encodeURIComponent(financingProgramId)}/${encodeURIComponent(creditApplicationId)}/CreditReport/${encodeURIComponent(creditReportId)}`
  const response = await apiClient.get<{ creditReport: unknown }>(url)
  return response.data
}

export function useGetCreditReport(
  financingProgramId: string | undefined,
  creditApplicationId: string | undefined,
  creditReportId: string | undefined,
): [{ creditReport: unknown } | null, boolean] {
  const { data, isFetching } = useQuery({
    queryKey: ['creditReport', financingProgramId, creditApplicationId, creditReportId] as const,
    queryFn: () => getCreditReportByCreditReportId(financingProgramId!, creditApplicationId!, creditReportId!),
    enabled: !!financingProgramId && !!creditApplicationId && !!creditReportId,
  })
  return [data ?? null, isFetching]
}

// ==================== CREDIT DECISION OVERRIDE LIMITS API ====================

const getCreditDecisionOverrideLimits = async (
  financingProgramId: string,
  creditApplicationId: string,
): Promise<unknown> => {
  const apiClient = getApiClient()
  const url = `/Applications/${encodeURIComponent(financingProgramId)}/${encodeURIComponent(creditApplicationId)}/DecisionOverrideLimits`
  const response = await apiClient.get<unknown>(url)
  return response.data
}

export function useGetCreditDecisionOverrideLimits(
  financingProgramId: string | undefined,
  creditApplicationId: string | undefined,
  enabled = true,
): [unknown, boolean] {
  const { data, isFetching } = useQuery({
    queryKey: ['creditDecisionOverrideLimits', financingProgramId, creditApplicationId] as const,
    queryFn: () => getCreditDecisionOverrideLimits(financingProgramId!, creditApplicationId!),
    enabled: enabled && !!financingProgramId && !!creditApplicationId,
  })
  return [data ?? null, isFetching]
}
